import { Flex, spacing, StyleProps, useColorMode } from 'deepstash-ui';
import { useRouter } from 'next/router';
import useAuth from 'providers/hooks/useAuth';
import useSource from 'providers/hooks/useSource';
import { IntersectionObserverContext } from 'providers/reads-autoincrement/IntersectionObserverProvider';
import React, { useCallback, useContext, useEffect } from 'react';
import IdeaCard from 'src/components/idea-card/IdeaCard';
import { Idea } from 'types/models';
import { GAP_SIZE_REM, getRemWidthFromColumns } from 'utils/global';
import { getSourceThemeColor } from 'utils/source.utils';
import SourceNSIArticleGradient from './SourceNSIArticleGradient';

const NSI_IDEA_SECTION_HEIGHT_UNDER_3 = 1500;
const NSI_IDEA_SECTION_HEIGHT_OVER_3 = 2000;

interface SourceCardIdeasProps {
  /**
   * Whether to show the Keep reading for FREE banner for NSI users
   * @default true
   */
  isNsiBlocked?: boolean;
  /**
   * Whether to allow interaction with the ideas
   * @default true
   */
  isInteractive?: boolean;
}

const SourceCardIdeas: React.FC<SourceCardIdeasProps & StyleProps> = ({
  isInteractive = true,
  isNsiBlocked = true,
  ...props
}) => {
  const { isLoggedIn } = useAuth();
  const { colorMode } = useColorMode();

  const { source, sourceDispatch } = useSource();

  const { setInsights } = useContext(IntersectionObserverContext);

  // Register ideas to the intersection observer to be eligible for auto-reading
  useEffect(() => {
    if (source) {
      setInsights(source.ideas);
    }
  }, [source?.ideas]);

  const router = useRouter();

  const onToggleSaveIdea = ({
    ideaId,
    isSaved,
  }: {
    ideaId: number;
    isSaved: boolean;
  }) => {
    sourceDispatch({
      type: 'toggle-save-idea',
      payload: {
        isSaved: isSaved,
        ideaId: ideaId,
      },
    });
  };

  const isIdeaBlockedNsi = useCallback(
    (index: number) => {
      if (source && source.ideas.length <= 2) {
        return index >= 1;
      } else return index >= 2;
    },
    [source?.ideas.length, router.asPath],
  );

  if (!source) return null;

  const mainLayoutSectionHeight =
    isLoggedIn || !isNsiBlocked
      ? 'unset'
      : (source?.ideas?.length ?? 0) < 3
      ? spacing.toRem(NSI_IDEA_SECTION_HEIGHT_UNDER_3)
      : spacing.toRem(NSI_IDEA_SECTION_HEIGHT_OVER_3);

  const renderIdeaCard = (idea: Idea, index: number) => (
    <section id={`idea_${idea.id}`}>
      <IdeaCard
        border={
          typeof window !== 'undefined' &&
          window.location.hash === `#idea_${idea.id}`
            ? `4px solid ${source.imageColor}`
            : 'none'
        }
        ideaData={{ idea, source }}
        preloadImage={true}
        w={{
          base: '100%',
          md: getRemWidthFromColumns(6),
        }}
        onIdeaStashed={() =>
          onToggleSaveIdea({ ideaId: idea.id, isSaved: true })
        }
        onIdeaUnstashed={() =>
          onToggleSaveIdea({ ideaId: idea.id, isSaved: false })
        }
        transition={'all 0.2s cubic-bezier(.43,.22,.05,.99)'}
        zIndex={1}
        minH="auto"
        isNotInteractable={!isInteractive}
        filter={
          isIdeaBlockedNsi(index) && !isLoggedIn && isNsiBlocked
            ? 'blur(4px)'
            : ''
        }
        userSelect={
          isIdeaBlockedNsi(index) && !isLoggedIn && isNsiBlocked
            ? 'none'
            : 'text'
        }
      />
    </section>
  );

  return (
    <Flex
      pos="relative"
      maxHeight={mainLayoutSectionHeight}
      overflowY={'hidden'}
      flexDirection="column"
      gridGap={GAP_SIZE_REM}
      ml={{ base: 0, lg: getRemWidthFromColumns(1) }}
      {...props}
    >
      {!isLoggedIn &&
        isNsiBlocked &&
        (source.ideaCount ?? 0) > 1 &&
        router.pathname !== '/idea/[id]/[slug]' && (
          <SourceNSIArticleGradient
            collection={source.seo?.recommended_collection} //source.seo.collection
            // We need this shift because we need to cover the 4px blur
            w={{
              base: '100%',
              md: `calc(${getRemWidthFromColumns(6)} + 8px)`,
            }}
            ml={{ base: 0, md: -4 }}
            underlineColor={getSourceThemeColor(source.sourceType, colorMode)}
          />
        )}
      {router.pathname === '/idea/[id]/[slug]' ? (
        <>
          {renderIdeaCard(source.ideas[0], 0)}
          {/* <SubsectionHeader mb={spacing.XXS.rem}>
            MORE IDEAS ON THIS
          </SubsectionHeader>
          {source.ideas.length > 1 &&
            source.ideas
              .slice(1)
              .map((idea, index) => renderIdeaCard(idea, index + 1))} */}
        </>
      ) : (
        source.ideas
          .sort((a, b) => a.order - b.order)
          .map((idea, index) => renderIdeaCard(idea, index))
      )}
    </Flex>
  );
};

export default SourceCardIdeas;
